import { useState } from "react";
import AdminLayout from "../components/AdminLayout";
import {
  AdminTable, AdminModal, ConfirmDialog, FormField,
  LoadingState, ErrorState, ApiModeBanner, Toggle, inputCls,
} from "../components/AdminUI";
import { useAdminT } from "../../../hooks/useAdminT";
import { useToast } from "../../../components/base/Toast";
import { useAdminData } from "../../../store/adminDataStore";
import type { BookingConfirmation, ConfirmationStatus } from "../../../store/types";

const NOW = () => new Date().toISOString();

const CONF_STATUSES: ConfirmationStatus[] = ["pending", "confirmed", "declined", "replacement_needed"];

const STATUS_STYLES: Record<ConfirmationStatus, string> = {
  pending: "bg-amber-50 text-amber-600",
  confirmed: "bg-emerald-50 text-emerald-600",
  declined: "bg-red-50 text-red-500",
  replacement_needed: "bg-orange-50 text-orange-600",
};

function emptyConfirmation(): BookingConfirmation {
  return {
    id: `bc-${Date.now()}`,
    order_id: "",
    photographer_id: "",
    confirmation_status: "pending",
    confirmed_at: null,
    declined_at: null,
    replacement_required: false,
    note: "",
    created_at: NOW(),
    updated_at: NOW(),
  };
}

function formatTimestamp(ts: string | null) {
  if (!ts) return "—";
  return new Date(ts).toLocaleString("zh-TW", { dateStyle: "short", timeStyle: "short" });
}

function EditModal({
  form, onChange, onClose, onSave, saving, isNew,
  orderOptions, photographerOptions,
}: {
  form: BookingConfirmation; onChange: (c: BookingConfirmation) => void;
  onClose: () => void; onSave: () => void; saving: boolean; isNew: boolean;
  orderOptions: Array<{ id: string; label: string }>;
  photographerOptions: Array<{ id: string; name: string }>;
}) {
  const t = useAdminT();
  const set = (field: keyof BookingConfirmation, value: unknown) => onChange({ ...form, [field]: value });

  return (
    <AdminModal
      title={isNew ? t("tabifotoAdmin.booking_confirmations.add_confirmation") : t("tabifotoAdmin.booking_confirmations.edit_confirmation")}
      onClose={onClose} onSave={onSave} saving={saving}
    >
      <FormField label={t("tabifotoAdmin.booking_confirmations.order_id")} required>
        <select className={inputCls} value={form.order_id} onChange={(e) => set("order_id", e.target.value)}>
          <option value="">—</option>
          {orderOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </select>
      </FormField>

      <FormField label={t("tabifotoAdmin.booking_confirmations.photographer_id")}>
        <select className={inputCls} value={form.photographer_id} onChange={(e) => set("photographer_id", e.target.value)}>
          <option value="">— {t("tabifotoAdmin.booking_confirmations.unassigned")} —</option>
          {photographerOptions.map((ph) => <option key={ph.id} value={ph.id}>{ph.name}</option>)}
        </select>
      </FormField>

      <FormField label={t("tabifotoAdmin.booking_confirmations.confirmation_status")} required>
        <select className={inputCls} value={form.confirmation_status} onChange={(e) => set("confirmation_status", e.target.value as ConfirmationStatus)}>
          {CONF_STATUSES.map((s) => (
            <option key={s} value={s}>{t(`tabifotoAdmin.booking_confirmations.status_${s}`)}</option>
          ))}
        </select>
      </FormField>

      <div className="flex items-center gap-3 mb-4">
        <Toggle
          checked={form.replacement_required}
          onChange={(v) => set("replacement_required", v)}
        />
        <span className="text-xs text-[#2C2C2C]/60">{t("tabifotoAdmin.booking_confirmations.replacement_required")}</span>
      </div>

      <FormField label={t("tabifotoAdmin.booking_confirmations.note")}>
        <textarea className={`${inputCls} h-20 resize-none`} value={form.note} onChange={(e) => set("note", e.target.value)} maxLength={500} />
      </FormField>
    </AdminModal>
  );
}

export default function AdminBookingConfirmationsPage() {
  const t = useAdminT();
  const { state, status, isApiMode, upsertConfirmation, removeConfirmation, refetchConfirmations } = useAdminData();
  const toast = useToast();

  const [editItem, setEditItem] = useState<BookingConfirmation | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<ConfirmationStatus | "all">("all");

  const orderOptions = state.orders.map((o) => ({
    id: o.id,
    label: `${o.order_number} — ${o.customer_name} (${o.shoot_date})`,
  }));

  const photographerOptions = state.photographers
    .filter((p) => p.is_active)
    .map((p) => ({ id: p.id, name: p.display_name }));

  const getOrderLabel = (id: string) => {
    const o = state.orders.find((x) => x.id === id);
    return o ? `${o.order_number} / ${o.customer_name}` : id || "—";
  };

  const getPhotographerName = (id: string) => {
    if (!id) return t("tabifotoAdmin.booking_confirmations.unassigned");
    return state.photographers.find((p) => p.id === id)?.display_name ?? id;
  };

  const filtered = [...state.bookingConfirmations]
    .filter((c) => filterStatus === "all" || c.confirmation_status === filterStatus)
    .sort((a, b) => a.created_at < b.created_at ? 1 : -1);

  const handleSave = async () => {
    if (!editItem) return;
    setSaving(true);
    try {
      await upsertConfirmation(editItem);
      toast.success(t("tabifotoAdmin.common.save_success"));
      setEditItem(null);
    } catch { toast.error(t("tabifotoAdmin.common.save_error")); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await removeConfirmation(deleteId);
      toast.success(t("tabifotoAdmin.common.delete_success"));
    } catch { toast.error(t("tabifotoAdmin.common.delete_error")); }
    finally { setDeleteId(null); }
  };

  const FILTER_TABS = [
    { key: "all" as const, label: t("tabifotoAdmin.booking_confirmations.filter_all") },
    { key: "pending" as const, label: t("tabifotoAdmin.booking_confirmations.status_pending") },
    { key: "confirmed" as const, label: t("tabifotoAdmin.booking_confirmations.status_confirmed") },
    { key: "declined" as const, label: t("tabifotoAdmin.booking_confirmations.status_declined") },
    { key: "replacement_needed" as const, label: t("tabifotoAdmin.booking_confirmations.status_replacement_needed") },
  ];

  const headers = [
    t("tabifotoAdmin.booking_confirmations.col_order"),
    t("tabifotoAdmin.booking_confirmations.col_photographer"),
    t("tabifotoAdmin.booking_confirmations.col_status"),
    t("tabifotoAdmin.booking_confirmations.col_confirmed_at"),
    t("tabifotoAdmin.booking_confirmations.col_replacement"),
    t("tabifotoAdmin.booking_confirmations.col_note"),
    t("tabifotoAdmin.booking_confirmations.col_actions"),
  ];

  return (
    <AdminLayout
      title={t("tabifotoAdmin.booking_confirmations.title")}
      subtitle={t("tabifotoAdmin.booking_confirmations.subtitle")}
      action={
        <button onClick={() => setEditItem(emptyConfirmation())}
          className="flex items-center gap-2 px-4 py-2 bg-[#2C2C2C] text-white text-sm rounded-md hover:bg-[#2C2C2C]/80 transition-colors cursor-pointer whitespace-nowrap">
          <div className="w-4 h-4 flex items-center justify-center"><i className="ri-add-line text-base" /></div>
          {t("tabifotoAdmin.booking_confirmations.add_confirmation")}
        </button>
      }
    >
      <ApiModeBanner isApiMode={isApiMode} />

      <div className="flex gap-1 bg-[#F7F6F3] p-1 rounded-lg w-fit mb-5 flex-wrap">
        {FILTER_TABS.map((tab) => (
          <button key={tab.key} onClick={() => setFilterStatus(tab.key)}
            className={`px-3 py-1.5 rounded-md text-xs font-light transition-colors cursor-pointer whitespace-nowrap ${filterStatus === tab.key ? "bg-white text-[#2C2C2C]" : "text-[#2C2C2C]/40 hover:text-[#2C2C2C]/70"}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {status.confirmations.loading ? <LoadingState /> :
        status.confirmations.error ? <ErrorState message={status.confirmations.error} onRetry={refetchConfirmations} /> : (
          <AdminTable headers={headers} empty={filtered.length === 0} emptyMessage={t("tabifotoAdmin.booking_confirmations.no_confirmations")}>
            {filtered.map((c) => (
              <tr key={c.id} className="border-b border-[#D9D9D9]/40 hover:bg-[#F7F6F3]/60 transition-colors">
                <td className="px-5 py-3">
                  <span className="text-xs font-mono text-[#2C2C2C]/60">{getOrderLabel(c.order_id)}</span>
                </td>
                <td className="px-5 py-3">
                  {c.photographer_id ? (
                    <span className="text-sm font-light text-[#2C2C2C]">{getPhotographerName(c.photographer_id)}</span>
                  ) : (
                    <span className="text-xs text-[#2C2C2C]/30 italic">{t("tabifotoAdmin.booking_confirmations.unassigned")}</span>
                  )}
                </td>
                <td className="px-5 py-3">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-light ${STATUS_STYLES[c.confirmation_status]}`}>
                    {t(`tabifotoAdmin.booking_confirmations.status_${c.confirmation_status}`)}
                  </span>
                </td>
                <td className="px-5 py-3 text-xs text-[#2C2C2C]/50">{formatTimestamp(c.confirmed_at)}</td>
                <td className="px-5 py-3">
                  {c.replacement_required ? (
                    <span className="flex items-center gap-1 text-xs text-orange-500">
                      <div className="w-3.5 h-3.5 flex items-center justify-center"><i className="ri-refresh-line" /></div>
                      {t("tabifotoAdmin.common.yes")}
                    </span>
                  ) : (
                    <span className="text-xs text-[#2C2C2C]/30">{t("tabifotoAdmin.common.no")}</span>
                  )}
                </td>
                <td className="px-5 py-3 max-w-[200px]">
                  {c.note ? <p className="text-xs text-[#2C2C2C]/50 line-clamp-2">{c.note}</p> : <span className="text-xs text-[#2C2C2C]/20">—</span>}
                </td>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => setEditItem({ ...c })}
                      className="w-7 h-7 flex items-center justify-center rounded-md text-[#2C2C2C]/40 hover:text-[#2C2C2C] hover:bg-[#F7F6F3] transition-colors cursor-pointer">
                      <i className="ri-edit-line text-base" />
                    </button>
                    <button onClick={() => setDeleteId(c.id)}
                      className="w-7 h-7 flex items-center justify-center rounded-md text-[#2C2C2C]/40 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer">
                      <i className="ri-delete-bin-line text-base" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </AdminTable>
        )}

      {editItem && (
        <EditModal form={editItem} onChange={setEditItem} onClose={() => setEditItem(null)}
          onSave={handleSave} saving={saving}
          isNew={!state.bookingConfirmations.some((c) => c.id === editItem.id)}
          orderOptions={orderOptions} photographerOptions={photographerOptions} />
      )}

      {deleteId && (
        <ConfirmDialog title={t("tabifotoAdmin.booking_confirmations.delete_confirm")}
          message={t("tabifotoAdmin.common.cannot_undone")} danger
          onConfirm={handleDelete} onCancel={() => setDeleteId(null)} />
      )}
    </AdminLayout>
  );
}
